import { Modal, Form, Input, InputNumber, Select, Row, Col, Button } from "antd";
import axios from "axios";
import { message } from "@bindeleddet/core/utils";
import { StudyProgram } from "@bindeleddet/core/types";
import { useEffect, useState } from "react";
import { PASSWORD_PATTERN } from "helpers/password";

const { Option } = Select;

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

const usernameRule = /^[a-zæøå0-9]+$/;

const fakeNumbers = [
  "12345678",
  "87654321",
  "12341234",
  "22225555",
  "11111111",
  "22222222",
  "33333333",
  "44444444",
  "55555555",
  "66666666",
  "77777777",
  "88888888",
  "99999999",
  "00000000",
  "43214321",
];

const CreateStudentModal = ({ open, onClose, onCreated }: Props) => {
  const [form] = Form.useForm();
  const [studyPrograms, setStudyPrograms] = useState<StudyProgram[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    axios
      .get<StudyProgram[]>("/students/study-program/")
      .then(res => setStudyPrograms(res.data.filter(program => program.name !== "Alle")));
  }, [open]);

  const onFinish = async (values: Record<string, unknown>) => {
    setLoading(true);
    const payload = {
      first_name: values.first_name,
      last_name: values.last_name,
      username: values.username,
      password: values.password,
      email: `${values.username}@stud.ntnu.no`,
      mobile_number: values.mobile_number,
      study_programme: values.study_programme,
      year_level: values.year_level,
      allergies: values.allergies,
    };
    try {
      await axios.post("/students/admin-create-student/", payload);
      message("success", "Student opprettet");
      onCreated();
      form.resetFields();
      onClose();
    } catch (error: unknown) {
      const respData = (error as { response?: { data?: { username?: unknown } } })?.response?.data;
      if (respData?.username) {
        message("error", "Brukernavnet er allerede i bruk");
      } else {
        message("error", "Kunne ikke opprette student");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} title="Opprett ny student" onCancel={onClose} footer={null} destroyOnClose>
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Row gutter={[16, 8]}>
          <Col span={12}>
            <Form.Item
              label="Brukernavn"
              name="username"
              rules={[
                { required: true, message: "Skriv inn brukernavn" },
                { pattern: usernameRule, message: "Kun små bokstaver (a-å) og tall" },
              ]}
            >
              <Input addonAfter="@stud.ntnu.no" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="mobile_number"
              label="Mobilnummer"
              rules={[
                { required: true, message: "Vennligst skriv inn telefonnummer" },
                {
                  validator: (_, value) => {
                    if (!value || value.toString().length !== 8) {
                      return Promise.reject(new Error("Telefonnummeret må være 8 sifre"));
                    }
                    if (fakeNumbers.includes(value.toString())) {
                      return Promise.reject(new Error("Skriv inn et gyldig telefonnummer"));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber style={{ width: "100%" }} maxLength={8} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[16, 8]}>
          <Col span={12}>
            <Form.Item
              name="first_name"
              label="Fornavn"
              rules={[{ required: true, message: "Vennligst skriv inn fornavn" }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="last_name"
              label="Etternavn"
              rules={[{ required: true, message: "Vennligst skriv inn etternavn" }]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[16, 8]}>
          <Col span={12}>
            <Form.Item
              name="study_programme"
              label="Linje"
              rules={[{ required: true, message: "Vennligst velg linje" }]}
            >
              <Select
                showSearch
                placeholder="Velg linje..."
                optionFilterProp="children"
                filterOption={(input, option) =>
                  ((option?.children as unknown) as string).toLowerCase().includes(input.toLowerCase())
                }
              >
                {studyPrograms.map(program => (
                  <Option key={program.id} value={program.id}>
                    {program.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="year_level"
              label="Årskull"
              rules={[{ required: true, message: "Vennligst velg årskull" }]}
            >
              <Select placeholder="Velg årstrinn...">
                <Option value={1}>1</Option>
                <Option value={2}>2</Option>
                <Option value={3}>3</Option>
                <Option value={4}>4</Option>
                <Option value={5}>5</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="allergies"
          label="Allergier"
          tooltip="Om du har allergier kan du skrive det inn her, skill med komma."
        >
          <Input />
        </Form.Item>

        <Row gutter={[16, 8]}>
          <Col span={12}>
            <Form.Item
              label="Passord"
              name="password"
              rules={[
                { required: true, message: "Vennligst fyll inn passord" },
                { pattern: PASSWORD_PATTERN, message: "Minst 8 tegn, én stor bokstav og ett tall" },
              ]}
              hasFeedback
            >
              <Input.Password />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Bekreft passord"
              name="confirm"
              dependencies={["password"]}
              hasFeedback
              rules={[
                { required: true, message: "Vennligst bekreft passordet" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) return Promise.resolve();
                    return Promise.reject(new Error("De to passordene samsvarer ikke"));
                  },
                }),
              ]}
            >
              <Input.Password />
            </Form.Item>
          </Col>
        </Row>

        <Row justify="end" gutter={8}>
          <Col>
            <Button onClick={onClose}>Avbryt</Button>
          </Col>
          <Col>
            <Button type="primary" htmlType="submit" loading={loading}>
              Opprett student
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default CreateStudentModal;
